import { createSelector } from "reselect"
import { get, find, chain, has, map, round } from "lodash"
import { getResidualTooltipContents } from "helpers/assets/tooltips"
import { percentageFormatter, numberFormatter } from "helpers/formatters/numbers"
import { getComparisonFactorsData, tableDownloadDataReducer } from "helpers/comparison"
import { getFavoriteFactors, getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { RESIDUAL_ITEM } from "helpers/residualAttribution"
import { getFactorsMetadata } from "selectors/factors"
import { getIsRfrAttributionEnabled } from "selectors/features"
import { VIEW_TYPES } from "helpers/base"
import { getModelCategories, getModelFactorsClassification } from "selectors/modelMetadata"
import { getSelectedMetric, getShowTradingPnl } from "./metrics"
import { getData0, getData1, getData2, getData3, getData4 } from "./data"
import { getItems } from "./items"

const getSelectedDate = (state, props) => props.selectedDate
const getViewType = (state, props) => props.viewType
const getMetrics0 = state => get(state, "global.data.comparePerformanceMetrics0.data.model.simulation")
const getMetrics1 = state => get(state, "global.data.comparePerformanceMetrics1.data.model.simulation")
const getMetrics2 = state => get(state, "global.data.comparePerformanceMetrics2.data.model.simulation")
const getMetrics3 = state => get(state, "global.data.comparePerformanceMetrics3.data.model.simulation")
const getMetrics4 = state => get(state, "global.data.comparePerformanceMetrics4.data.model.simulation")

function getDailyData(selectedDate, data, metrics, categories, mustAggregateFactors, factorsClassification, isRfrAttributionEnabled) {
  const date = find(data, d => d.date === selectedDate)
  if (!date) {
    return null
  }

  let factors = get(date, "attribution.factors")
  if (mustAggregateFactors) {
    factors = aggregateFactorsSumBy(factorsClassification, factors, "value")
  }

  const ppQueryCompleted = has(metrics, "periodPerformance")
  const defaultPeriodPerformance = ppQueryCompleted ? null : "Loading..."
  const resultMetrics = {
    maxDrawdown: get(metrics, "periodPerformance.maxDrawdown.drawdown", defaultPeriodPerformance),
    sortinoRatio: get(metrics, "periodPerformance.sortinoRatio", defaultPeriodPerformance),
    informationRatio: get(metrics, "periodPerformance.informationRatio", defaultPeriodPerformance),
    annualizedVolatility: get(metrics, "periodPerformance.annualizedVolatility", defaultPeriodPerformance),
    annualizedReturns: get(metrics, "periodPerformance.annualizedReturns", defaultPeriodPerformance),
    correlation: get(metrics, "correlation", "Loading..."),
  }

  const result = {
    summary: {
      total: date.total,
      factors: date.attribution.summary.factors,
      specific: date.attribution.summary.specific,
      trading: date.attribution.summary.trading,
      ...(isRfrAttributionEnabled ? { residual: date.attribution.summary.residual } : {}),
    },
    metrics: resultMetrics,
    attribution: get(metrics, "periodPerformance.attribution"),
    categories: chain(categories)
      .map(category => ({
        id: category.id,
        value: chain(factors)
          .filter(f => f.category === category.id)
          .sumBy("value")
          .value(),
      }))
      .keyBy("id")
      .mapValues("value")
      .value(),
    factors: chain(factors)
      .keyBy("id")
      .mapValues("value")
      .value(),
  }

  return result
}

const getDailyData0 = createSelector([getSelectedDate, getData0, getMetrics0, getModelCategories, getMustAggregateFactors, getModelFactorsClassification, getIsRfrAttributionEnabled], getDailyData)
const getDailyData1 = createSelector([getSelectedDate, getData1, getMetrics1, getModelCategories, getMustAggregateFactors, getModelFactorsClassification, getIsRfrAttributionEnabled], getDailyData)
const getDailyData2 = createSelector([getSelectedDate, getData2, getMetrics2, getModelCategories, getMustAggregateFactors, getModelFactorsClassification, getIsRfrAttributionEnabled], getDailyData)
const getDailyData3 = createSelector([getSelectedDate, getData3, getMetrics3, getModelCategories, getMustAggregateFactors, getModelFactorsClassification, getIsRfrAttributionEnabled], getDailyData)
const getDailyData4 = createSelector([getSelectedDate, getData4, getMetrics4, getModelCategories, getMustAggregateFactors, getModelFactorsClassification, getIsRfrAttributionEnabled], getDailyData)

function formatWithLoading(item, key, formatter, ...args) {
  const value = get(item, key)
  if (value === "Loading..." || !value) {
    return value
  } else {
    return formatter(value, ...args)
  }
}

const getFactorsData = createSelector(
  [getSelectedDate, getModelCategories, getFavoriteFactors, getData0, getData1, getData2, getData3, getData4],
  (selectedDate, categories, favoriteFactors, data, data1, data2, data3, data4) =>
    getComparisonFactorsData(selectedDate, categories, favoriteFactors, "attribution.factors", data, data1, data2, data3, data4),
)

function getInformationRatioLabel(viewType) {
  return viewType === VIEW_TYPES.ACTIVE ? "Information ratio" : "Sharpe"
}

function getAnnualizedItemKey({ section, selectedMetric, metric }) {
  if (section === "total") {
    return `metrics.${metric}`
  }
  if (!selectedMetric) {
    return `attribution.summary.${section}.${metric}`
  }
  return metric
}

function getAnnualizedMetricsItems({ viewType, section, selectedMetric }) {
  const wrapItem = item => !selectedMetric ? item : find(item.attribution?.factors, ({ id }) => id === selectedMetric.id)
  const annualizedReturnsKey = getAnnualizedItemKey({ section, selectedMetric, metric: "annualizedReturns" })
  const annualizedVolatilityKey = getAnnualizedItemKey({ section, selectedMetric, metric: "annualizedVolatility" })
  const informationRatioKey = getAnnualizedItemKey({ section, selectedMetric, metric: "informationRatio" })
  return [{
    label: "Annualized Returns",
    fn: item => formatWithLoading(wrapItem(item), annualizedReturnsKey, percentageFormatter, true),
  }, {
    label: "Annualized Volatility",
    fn: item => formatWithLoading(wrapItem(item), annualizedVolatilityKey, percentageFormatter, true),
  }, ...(section !== RESIDUAL_ITEM.id ? [{
    label: getInformationRatioLabel(viewType),
    fn: item => formatWithLoading(wrapItem(item), informationRatioKey, numberFormatter),
  }] : [])]
}

export const getDetailsItems = createSelector(
  [getFactorsMetadata, getSelectedMetric, getViewType, getShowTradingPnl, getIsRfrAttributionEnabled],
  (factors, selectedMetric, viewType, showTradingPnl, isRfrAttributionEnabled) => {
    const totalReturnsSection = {
      label: "Total Return",
      defaultOpen: true,
      fn: item => percentageFormatter(get(item, "summary.total", 0)),
      tooltipContentsFn: item => {
        const residual = get(item, "summary.residual", 0)
        // Rounding the residual to 4 decimal places avoids showing 0.00% when residual is < 0.005%
        if (isRfrAttributionEnabled && isFinite(residual) && round(residual, 4) !== 0) {
          return getResidualTooltipContents(percentageFormatter(residual))
        }
        return null
      },
      items: [
        ...getAnnualizedMetricsItems({ viewType, section: "total" }),
        {
          label: "Sortino Ratio",
          fn: item => formatWithLoading(item, "metrics.sortinoRatio", numberFormatter),
        }, {
          label: "Returns Correlation",
          fn: item => formatWithLoading(item, "metrics.correlation", numberFormatter),
        }, {
          label: "Max Drawdown",
          fn: item => formatWithLoading(item, "metrics.maxDrawdown", percentageFormatter, true),
        }],
    }

    const alphaSection = {
      label: "Alpha",
      fixGap: true,
      fn: item => percentageFormatter(get(item, "summary.specific", 0)),
      items: getAnnualizedMetricsItems({ viewType, section: "specific" }),
    }

    const factorsSection = {
      label: "Factors",
      fn: item => percentageFormatter(get(item, "summary.factors", 0)),
      items: getAnnualizedMetricsItems({ viewType, section: "factors" }),
    }

    const tradingPNLSections = showTradingPnl ? [{
      label: "Trading",
      fixGap: true,
      fn: item => percentageFormatter(get(item, "summary.trading", 0)),
    }] : []

    const selectedFactorSections = factors[selectedMetric.id] ? [{
      label: selectedMetric.label,
      defaultOpen: true,
      fixGap: true,
      fn: item => percentageFormatter(get(item, ["factors", selectedMetric.id], 0)),
      ...(selectedMetric ? { items: getAnnualizedMetricsItems({ viewType, selectedMetric }) } : {}),
    }] : []

    return [
      totalReturnsSection,
      alphaSection,
      factorsSection,
      ...tradingPNLSections,
      ...selectedFactorSections,
    ]
  },
)

export const getFactorsCategories = createSelector(
  [getModelCategories, getFactorsData, getFavoriteFactors],
  (categories, factors, favoriteFactors) => chain(categories)
    .filter(category => category.id !== "esg")
    .map((category) => ({
      ...category,
      getter: item => percentageFormatter(get(item, ["categories", category.id], 0)),
      factors: chain(factors)
        .get(category.id)
        .map(factor => ({
          ...factor,
          getter: factor.fn,
          favorite: get(favoriteFactors, factor.id, false),
        }))
        .orderBy(["favorite", f => f.priorityOrder || 0, f => Math.abs(f.value)], ["desc", "desc", "desc"])
        .value(),
    }))
    .value(),
)

export const getDetailsData = createSelector(
  [getDailyData0, getDailyData1, getDailyData2, getDailyData3, getDailyData4],
  (data0, data1, data2, data3, data4) => ([data0, data1, data2, data3, data4]),
)

export const getTableDetailsData = createSelector(
  [getItems, getDetailsData, getDetailsItems],
  (items, data, detailsItems) => tableDownloadDataReducer(items, data, detailsItems, "items"),
)

const getTableFactorsData = createSelector(
  [getItems, getDetailsData, getFactorsCategories],
  (items, data, factorsCategories) => tableDownloadDataReducer(items, data, factorsCategories, "factors"),
)

export const getTableDownloadData = createSelector(
  [getItems, getTableDetailsData, getTableFactorsData],
  (items, tableDetailsData, tableFactorsData) => {
    const headers = ["Metric Group", "Metric", ...map(items, "label")]
    return {
      sheets: [{
        name: "Performance",
        rows: [
          headers,
          ...tableDetailsData,
          ...tableFactorsData,
        ],
      }],
    }
  },
)
